import { faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../../Icon";
import type { ReactNode } from "react";
import type { Compendium } from "@lancer-vtt/compendium";
import { HIDDEN_STATUS, type CombatantId, type DisplayCombatant, type EncounterState, type PilotData, type Side, type StatusInstance } from "@lancer-vtt/domain";
import { RulesText } from "../../RulesText";
import { isMaskedCombatant } from "../disclosure";
import { StatusChip } from "../StatusChip";
import { StatusAddForm, type EncounterDispatch } from "../StatusEditor";
import { decrementRulingCommand, removeStatusCommand } from "../status";
import type { ControlPanelSlot } from "./ControlPanel";
import "./unit-sheet.css";

/**
 * The **unit sheet** (`unit-sheet-redesign`, direction A "Card"): one sheet for every mech, NPC and
 * pilot in COMBAT.app PLAY. The command menu runs a unit's turn and the token carries its state at a
 * glance, so the sheet is where a unit is *read*. It lays its sections out in one column, in the
 * design's order: header, state line, banners, then the body (vitals, stats, statuses, loadout),
 * then the GM strip.
 *
 * This component is the shell. It owns the header, the state line, the banners and the GM strip's
 * place at the foot; the per-kind
 * bodies (`MechInspectorBody`, `NpcInspectorBody`, `PlayerNpcInspectorBody`, `PilotInspectorBody`)
 * fill the slots and render their remaining content as `children`. The layout reflows on the window
 * body's width through a container query on `.unit-sheet`, never the viewport, because the window
 * sheet opens at 500px (single column) and the lane can clamp it to the 360px floor at 1280 (`decisions/0191`).
 */
export function UnitSheet({
  identity,
  stateLine,
  error,
  hold,
  band,
  gm,
  className,
  posture = "wear",
  art,
  badges,
  actions,
  nameEditor,
  toolbar,
  children,
}: {
  identity: UnitSheetIdentity;
  /**
   * WEAR is the fight's sheet: the scrolling `gms-content` host of a combat window. BUILD is the
   * roster's (`decisions/0206`, the PILOT, MECH and SQUAD pages): it sits inside the page's own
   * scroller, and its header gains the art slot and the page's actions.
   */
  posture?: "wear" | "build";
  /** BUILD: the frame art or the pilot portrait, left of the name. */
  art?: ReactNode;
  /** Badges after the kind line (BUILD: ACTIVE on the pilot's active mech). */
  badges?: ReactNode;
  /** BUILD: the page's controls, top right above the side tag (HANGAR, EDIT). */
  actions?: ReactNode;
  /** BUILD in EDIT mode: the name input, in the name's place. */
  nameEditor?: ReactNode;
  /** BUILD in EDIT mode: the `EDITING · AUTO-SAVED` bar, under the header. */
  toolbar?: ReactNode;
  /**
   * The state line's content: `TurnControls` for a unit the viewer can see in full, the bare
   * `TurnStateText` for a masked hostile, or the state text plus the granted-turn fold. Absent for a
   * unit that takes no turns (an object's sheet): the line then shows only for the HIDDEN chip.
   */
  stateLine?: ReactNode;
  error?: string | null;
  /** The prepared-hold banner (`PreparedHoldPanel`), when the unit holds one. */
  hold?: ReactNode;
  /** A band across the top of the sheet: the GM's AS PLAYERS SEE IT notice. */
  band?: ReactNode;
  /** The GM strip (`GmStrip`), at the sheet's foot. The GM's sheet only. */
  gm?: ReactNode;
  /** The body root's extra class: the old body's own, so its sections keep their styling. */
  className?: string;
  children?: ReactNode;
}) {
  const { name, kindLine, level, tier, templates, terse, side, tag, hidden } = identity;
  return (
    <div className={`${posture === "build" ? "is-build" : "gms-content"} unit-sheet${className ? ` ${className}` : ""}`}>
      {band}
      <header className={`unit-sheet-head${art ? " has-art" : ""}`}>
        {art && <div className="unit-sheet-art">{art}</div>}
        <div className="unit-sheet-head-main">
          {nameEditor ?? <span className="unit-sheet-name">{name}</span>}
          <div className="unit-sheet-sub">
            {kindLine.length > 0 && <span className="unit-sheet-kindline">{kindLine.join(" · ")}</span>}
            {level !== undefined && <span className="unit-sheet-level">LL {level}</span>}
            {tier !== undefined && <span className="unit-sheet-tier">T{tier}</span>}
            {templates.map((t) => (
              <span key={t.id} className="unit-sheet-tmpl" title={`Applied template: ${t.name}`}>
                {t.name}
              </span>
            ))}
            {badges}
          </div>
          {terse && <RulesText className="unit-sheet-terse" html={terse} />}
        </div>
        <div className="unit-sheet-head-side">
          {actions && <div className="unit-sheet-actions">{actions}</div>}
          {tag !== undefined ? <span className="unit-sheet-side is-neutral">{tag}</span> : <span className={`unit-sheet-side is-${side}`}>{side === "pc" ? "ALLY" : "HOSTILE"}</span>}
        </div>
      </header>
      {toolbar}
      {(stateLine !== undefined || hidden) && (
        <div className="unit-sheet-state">
          {stateLine}
          {hidden && <span className="unit-sheet-hidden status-holo">
              <Icon icon={faEyeSlash} className="icon-lead" />
              HIDDEN
            </span>}
          <span className="unit-sheet-state-gap" aria-hidden />
        </div>
      )}
      {error && <div className="unit-sheet-error">Rejected: {error}</div>}
      {hold}
      {children}
      {gm}
    </div>
  );
}

/**
 * The **statuses row** (3.3): `StatusChip`s, each opening its rule. An active GM ruling wears the
 * outlined variant, so a GM-granted bonus reads apart from a rules condition.
 *
 * For a player the row is read-only, and absent when the unit carries none: an empty "none" row on
 * every quiet unit is noise. For the GM (`edit` set) each chip carries a remove `×` and the shared
 * {@link StatusAddForm} sits under the chips, so the GM adds and removes any status on any unit
 * without leaving PLAY. Each gesture is one `apply_status` / `remove_status`, so one undo step.
 */
export function UnitSheetStatuses({
  combatant: c,
  edit,
}: {
  combatant: Pick<DisplayCombatant, "id" | "statuses">;
  /** The GM's editing hooks; `null` renders the read-only row. */
  edit: { state: EncounterState; actorId: string; dispatch: EncounterDispatch } | null;
}) {
  if (!edit && c.statuses.length === 0) return null;
  return (
    <div className={`unit-sheet-statuses${edit ? " is-editable" : ""}`}>
      {c.statuses.length > 0 && (
        <ul className="unit-sheet-status-list" aria-label="statuses">
          {c.statuses.map((s) => (
            <StatusChip
              key={s.name}
              status={s}
              className={`unit-sheet-status${s.ruling ? " is-ruling" : ""}`}
              onRemove={edit ? () => edit.dispatch(removeStatusCommand({ actorId: edit.actorId, combatantId: c.id, status: s.name })) : undefined}
              onDecrement={edit ? () => dispatchDecrement(edit, c.id, s) : undefined}
            />
          ))}
        </ul>
      )}
      {edit && <StatusAddForm combatantId={c.id} state={edit.state} actorId={edit.actorId} dispatch={edit.dispatch} />}
    </div>
  );
}

/** Take one level off a stacked ruling — see {@link decrementRulingCommand}. */
function dispatchDecrement(edit: { actorId: string; dispatch: EncounterDispatch }, combatantId: CombatantId, status: StatusInstance): void {
  const command = decrementRulingCommand({ actorId: edit.actorId, combatantId, status });
  if (command !== undefined) edit.dispatch(command);
}

/** What the sheet's header says about a unit: everything here is public at every disclosure tier. */
export type UnitSheetIdentity = {
  name: string;
  /**
   * The line under the name, joined with ` · `. Mech: `FRAME · CALLSIGN · PLAYER`. NPC:
   * `CLASS · ROLE`. Pilot: `PILOT · CALLSIGN · PLAYER`. A part that doesn't resolve is left out.
   */
  kindLine: string[];
  /** A pilot's license level. */
  level?: number;
  tier?: number;
  templates: { id: string; name: string }[];
  /** An NPC class's one-line `info.terse`, public at every tier (RAW p.283). */
  terse?: string;
  side: Side;
  /**
   * A neutral tag shown in place of ALLY/HOSTILE: an object's or deployable's kind. Scenery reads
   * neutral whatever its allegiance, as its board token does (`spawnObject` stamps `npc` on every
   * crate as a default nobody chose).
   */
  tag?: string;
  /** The unit carries the RAW Hidden status: the state line gets `hidden-token-visual`'s holo chip. */
  hidden: boolean;
};

/**
 * Derive a unit's {@link UnitSheetIdentity}. Reads only fields a masked projection keeps
 * (`MaskedKeepFields`, plus `base.allegiance`), so it is safe on a hostile at the `class` tier.
 * Lookups use `getById` and degrade: a class, frame or template whose pack the viewer lacks drops
 * out of the line, or shows its bare id for a template badge, rather than throwing into the sheet.
 */
export function unitSheetIdentity({
  combatant: c,
  compendium,
  pilots,
  slots,
}: {
  combatant: DisplayCombatant;
  compendium: Compendium;
  /** The roster's pilots by id, for a pilot's full name, callsign and license level. */
  pilots: Record<string, PilotData>;
  /** The game's slots, for the controlling player's name. */
  slots: ControlPanelSlot[];
}): UnitSheetIdentity {
  const player = slots.find((s) => s.id === c.controllerSlotId && s.role === "player")?.name;
  const templates = (c.templateIds ?? []).map((id) => ({
    id,
    name: (compendium.getById("npc_template", id) as { name?: string } | undefined)?.name ?? id,
  }));
  const common = { templates, side: c.base.allegiance, hidden: c.statuses.some((s) => s.name === HIDDEN_STATUS) };
  const parts = (...xs: (string | undefined)[]) => xs.filter((x): x is string => !!x);

  if (c.kind === "npc") {
    const npcClass = c.sourceRef ? compendium.getById("npc_class", c.sourceRef) : undefined;
    return {
      ...common,
      name: c.name,
      // The template-forced tag (Vehicle, Ship) rides the unmasked base only; a masked card shows class and role.
      kindLine: parts(npcClass?.name, npcClass?.role, "npcTag" in c.base ? c.base.npcTag?.toUpperCase() : undefined),
      tier: c.tier,
      terse: npcClass?.info?.terse,
    };
  }
  if (c.kind === "pilot") {
    // A pilot combatant's `sourceRef` is its roster pilot's id, and its `name` is the callsign.
    const pilot = c.sourceRef ? pilots[c.sourceRef] : undefined;
    return {
      ...common,
      name: pilot?.name || c.name,
      kindLine: parts("PILOT", pilot?.callsign || c.name, player),
      level: pilot?.level,
    };
  }
  // A mech's `sourceRef` is its frame id. Its callsign is frozen on `base.pilotBlock`; a mech is
  // never masked (`projectCombatantFor` returns every pc-side unit whole), but the type says it
  // could be, so the read is narrowed.
  const frame = c.sourceRef ? compendium.getById("frame", c.sourceRef) : undefined;
  const callsign = isMaskedCombatant(c) ? undefined : c.base.pilotBlock?.callsign;
  return { ...common, name: c.name, kindLine: parts(frame?.name, callsign, player) };
}
